// Sidebar navigation for the org-config page. Presentational and self-contained
// so it can be rendered in isolation (see stories/OrgConfigNav.stories.tsx) —
// the page owns which section is active and which ones are dirty.
//
// The grouping carries meaning beyond tidiness: the first groups are sections of
// one form sharing a Save/Reset footer, while a group flagged
// `savesIndependently` holds panels that write on their own. The page hides the
// footer for those, and the caption tells the reader why they behave differently.
import { ReactNode, useMemo, useState } from "react";

import { Clear, FiberManualRecord, Search } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

export type ConfigNavGroup<K extends string> = {
  title: string;
  caption?: string;
  savesIndependently?: boolean;
  tabs: K[];
};

/** One searchable field: its column/metadata key, the section that owns it, and
 *  its help text if there is one (searched too, so prose finds the field). */
export type ConfigFieldIndexEntry<K extends string> = {
  field: string;
  tab: K;
  help?: string;
};

// Underscores read as word breaks so "badge number" finds
// rms_export_badge_number_override.
const haystack = <K extends string>(e: ConfigFieldIndexEntry<K>) =>
  `${e.field} ${e.field.replace(/_/g, " ")} ${e.help ?? ""}`.toLowerCase();

export function ConfigSectionNav<K extends string>({
  groups,
  labels,
  active,
  dirty,
  onSelect,
  fieldIndex = [],
}: {
  groups: ConfigNavGroup<K>[];
  labels: Record<K, string>;
  active: K;
  dirty: ReadonlySet<K>;
  /** `field` is set only when the choice came from a search hit, so the page can
   *  scroll to that input rather than just opening its section. */
  onSelect: (key: K, field?: string) => void;
  /** Enables the search box. Omit it and the nav renders groups only. */
  fieldIndex?: ConfigFieldIndexEntry<K>[];
}) {
  const [query, setQuery] = useState("");
  const trimmed = query.trim().toLowerCase();

  // Search covers fields, not section names — the group list already answers
  // "which section", and the hard question is which section owns a given field.
  const results = useMemo(() => {
    if (trimmed.length < 2) return [];
    return fieldIndex
      .filter((e) => haystack(e).includes(trimmed))
      .sort((a, b) => {
        // Name matches before help-text-only matches, then alphabetical.
        const an = a.field.replace(/_/g, " ").includes(trimmed) ? 0 : 1;
        const bn = b.field.replace(/_/g, " ").includes(trimmed) ? 0 : 1;
        return an - bn || a.field.localeCompare(b.field);
      })
      .slice(0, 30);
  }, [fieldIndex, trimmed]);

  const label = (key: K): ReactNode => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      component="span"
      sx={{ textTransform: "none" }}
    >
      <span>{labels[key]}</span>
      {dirty.has(key) && (
        <FiberManualRecord
          sx={{ fontSize: 10, color: "warning.main" }}
          aria-label="unsaved changes"
        />
      )}
    </Stack>
  );

  return (
    <Box
      component="nav"
      aria-label="Org config sections"
      sx={{
        width: { xs: "100%", md: 232 },
        flexShrink: 0,
        // Sections run long; keep the nav visible while the pane scrolls.
        position: { md: "sticky" },
        top: { md: 16 },
      }}
    >
      {fieldIndex.length > 0 && (
        <TextField
          fullWidth
          size="small"
          type="search"
          placeholder="Find a field…"
          name="org-config-field-filter"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setQuery("");
            // Enter on a single hit is the common case — take it.
            if (e.key === "Enter" && results.length === 1) {
              onSelect(results[0].tab, results[0].field);
              setQuery("");
            }
          }}
          sx={{
            mb: 1,
            "& input::-webkit-search-cancel-button": { display: "none" },
          }}
          slotProps={{
            htmlInput: {
              autoComplete: "off",
              spellCheck: false,
              autoCorrect: "off",
              autoCapitalize: "off",
              "data-1p-ignore": true, // 1Password
              "data-lpignore": true, // LastPass
              "data-form-type": "other", // Dashlane
              "data-bwignore": true, // Bitwarden
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear field search"
                    onClick={() => setQuery("")}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
      )}

      {trimmed.length >= 2 ? (
        <Box>
          <Typography
            variant="caption"
            sx={{ display: "block", px: 2, py: 0.5, color: "text.secondary" }}
          >
            {results.length === 0
              ? "No matching field"
              : `${results.length} field${results.length === 1 ? "" : "s"} — opens its section`}
          </Typography>
          <List dense disablePadding>
            {results.map((r) => (
              <ListItemButton
                key={`${r.tab}:${r.field}`}
                onClick={() => {
                  onSelect(r.tab, r.field);
                  setQuery("");
                }}
                sx={{ borderRadius: 1, pl: 2, py: 0.25, display: "block" }}
              >
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  {r.field}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {labels[r.tab]}
                </Typography>
              </ListItemButton>
            ))}
          </List>
        </Box>
      ) : (
        <List dense disablePadding>
          {groups.map((group) => (
            <li key={group.title}>
              <ListSubheader
                disableSticky
                sx={{
                  lineHeight: 1.4,
                  pt: 1.25,
                  pb: 0.25,
                  bgcolor: "transparent",
                  fontSize: 12,
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                }}
              >
                {group.title}
                {group.caption && (
                  <Typography
                    variant="caption"
                    // The group heading is uppercased; the caption is a sentence
                    // and shouldn't inherit that.
                    sx={{
                      display: "block",
                      color: "text.disabled",
                      textTransform: "none",
                      letterSpacing: 0,
                    }}
                  >
                    {group.caption}
                  </Typography>
                )}
              </ListSubheader>
              <List dense disablePadding>
                {group.tabs.map((key) => (
                  <ListItemButton
                    key={key}
                    selected={active === key}
                    onClick={() => onSelect(key)}
                    // Tight rhythm so all 16 destinations fit without scrolling —
                    // seeing them at once is the point of the sidebar.
                    sx={{ borderRadius: 1, pl: 2, py: 0.25, minHeight: 30 }}
                  >
                    <ListItemText
                      primary={label(key)}
                      slotProps={{ primary: { variant: "body2" } }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </li>
          ))}
        </List>
      )}
    </Box>
  );
}
